/**
 * Akar aplikasi: penyedia yang dipakai KEDUA tampilan, lalu pemilih tampilan.
 *
 * Bahasa, klien kueri, dan sesi dipasang SEKALI di sini, jadi kedua tampilan membaca
 * sesi dan bahasa yang sama. Tampilan mana yang dipakai ditentukan `tampilan.js`.
 *
 * Rancang ulang dimuat MALAS. Selama sebuah aplikasi belum dipindah, orang yang
 * memakainya tidak ikut mengunduh pustaka dan font milik tampilan baru.
 */
import { lazy, Suspense, useLayoutEffect, useRef } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { PenyediaBahasa } from './i18n'
import { PenyediaSesi, useSesi } from './auth'
import { klienKueri } from './kueri'
import { muatUlangHalaman, pakaiTampilanBaru } from './tampilan'
import { bacaTema, pasangTema } from './tema'
import { ALAMAT_MASUK, lanjutAman } from '../kerangka/rute'
import App from './App'

const AppBaru = lazy(() => import('../kerangka/AppBaru'))

/**
 * `/masuk` hanya ada di rancang ulang; tampilan lama menampilkan layar masuknya di alamat
 * mana pun. Orang yang masuk lewat layar baru sementara aplikasinya belum pindah akan
 * tiba di tampilan lama dengan alamat `/masuk?lanjut=…`, dan pohon rute lama membuangnya
 * ke beranda. Alamatnya dibetulkan dulu.
 */
function tinggalkanAlamatMasuk() {
  if (window.location.pathname !== ALAMAT_MASUK) return
  const lanjut = new URLSearchParams(window.location.search).get('lanjut')
  window.history.replaceState(null, '', lanjutAman(lanjut) ?? '/')
}

/**
 * Keluar dari tampilan lama di `/`: sesudah dimuat ulang, `/` di rancang ulang adalah landing
 * page (ADR-126), padahal tombolnya "Keluar dan masuk lagi". Yang tiba di sini sesi berperan
 * tidak dikenal, satu-satunya yang masih memakai tampilan lama tanpa `?ui=lama`.
 */
function keLayarMasukSesudahKeluar() {
  if (window.location.pathname === '/') window.history.replaceState(null, '', ALAMAT_MASUK)
}

function Pemilih() {
  const { sesi, sesiTerakhir } = useSesi()
  // Sesi yang habis tetap menentukan tampilannya: halaman di bawah dialog masuk lagi
  // tidak boleh berganti rupa.
  const peran = (sesi ?? sesiTerakhir)?.peran ?? null
  const baru = pakaiTampilanBaru(peran)

  // GANTI TAMPILAN = MUAT ULANG HALAMAN. Tiap tampilan punya router sendiri, dan dua
  // router yang bergantian memegang satu riwayat peramban akan saling membaca alamat
  // basi. Ini hanya terjadi saat masuk atau keluar selagi satu aplikasi sudah pindah
  // dan yang lain belum; sesinya ada di penyimpanan, jadi tidak ada yang hilang.
  const awal = useRef(baru)
  const berganti = awal.current !== baru
  useLayoutEffect(() => {
    if (!berganti) return
    if (!baru) tinggalkanAlamatMasuk()
    else if (!peran) keLayarMasukSesudahKeluar()
    muatUlangHalaman()
  }, [berganti, baru, peran])

  // Token warna dan font rancang ulang hanya berlaku di bawah `data-ui="baru"`
  // (`gaya/baru.css`). Efek TATA LETAK, supaya atributnya terpasang sebelum gambar
  // pertama dan tidak ada kedipan warna lama. Tema pilihan orangnya (`data-tema`, ADR-116)
  // ikut di sini, dan hanya untuk tampilan baru.
  useLayoutEffect(() => {
    if (berganti) return
    const akar = document.documentElement
    if (!baru) {
      delete akar.dataset.ui
      delete akar.dataset.peran
      delete akar.dataset.tema
      return
    }
    akar.dataset.ui = 'baru'
    pasangTema(bacaTema(), akar)
    if (peran) akar.dataset.peran = peran
    else delete akar.dataset.peran
  }, [berganti, baru, peran])

  if (berganti) return null
  if (!baru) return <App />
  return (
    <Suspense fallback={null}>
      <AppBaru />
    </Suspense>
  )
}

export default function Akar() {
  return (
    <PenyediaBahasa>
      <QueryClientProvider client={klienKueri}>
        <PenyediaSesi>
          <Pemilih />
        </PenyediaSesi>
      </QueryClientProvider>
    </PenyediaBahasa>
  )
}
